import type {
  CreateServiceStandardFormDTO,
  UpdateServiceStandardFormDTO,
} from "@/modules/service-standard/domain/contracts";
import type {
  ServiceStandardByIdResult,
  ServiceStandardsListResult,
} from "@/modules/service-standard/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import { fetchLookup, hydrateLookupItem } from "@/shared/lib/utils";

const baseUrl = `${config.apiUrl}/service-standard`;

export const serviceStandardApi = {
  async list(
    query: Partial<OffsetPageQueryDTO> = {},
  ): Promise<ServiceStandardsListResult> {
    const url = new URL(baseUrl);
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.offset) url.searchParams.set("offset", String(query.offset));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<ServiceStandardsListResult>(url.toString());
  },

  async get(id: string): Promise<ServiceStandardByIdResult> {
    return fetcher.get<ServiceStandardByIdResult>(`${baseUrl}/${id}`);
  },

  async create(input: CreateServiceStandardFormDTO): Promise<ServiceStandardByIdResult> {
    return fetcher.post<ServiceStandardByIdResult>(baseUrl, input);
  },

  async duplicate(id: string): Promise<ServiceStandardByIdResult> {
    return fetcher.post<ServiceStandardByIdResult>(`${baseUrl}/${id}/duplicate`, {});
  },

  async update(
    id: string,
    input: UpdateServiceStandardFormDTO,
  ): Promise<ServiceStandardByIdResult> {
    return fetcher.put<ServiceStandardByIdResult>(`${baseUrl}/${id}`, input);
  },

  async delete(id: string): Promise<{ ok: true }> {
    return fetcher.delete<{ ok: true }>(`${baseUrl}/${id}`);
  },
};

export type ServiceStandardMappingDTO = {
  id: string;
  serviceStandardId: string;
  serviceStandardName?: string | null;
  positionId: string | null;
  positionTitle: string | null;
  checklistId: string | null;
  checklistName: string | null;
  qcStandardId: string | null;
  qcStandardName: string | null;
  kpiId: string | null;
  kpiName: string | null;
  createdAt: string;
};

export type ServiceStandardMappingListResult = {
  data: ServiceStandardMappingDTO[];
  meta: { total: number; limit: number; offset: number };
};

export const serviceStandardMappingApi = {
  async listMappings(
    query: Partial<{
      offset: number;
      limit: number;
      sort?: Array<{ field: string; dir: "asc" | "desc" }>;
      filters?: Array<{ field: string; op: string; value: unknown }>;
      serviceStandardId?: string;
      positionId?: string;
      checklistId?: string;
      qcStandardId?: string;
      kpiId?: string;
      search?: string;
    }> = {},
  ): Promise<ServiceStandardMappingListResult> {
    const url = new URL(`${baseUrl}/mappings`);
    if (query.offset !== undefined) url.searchParams.set("offset", String(query.offset));
    if (query.limit !== undefined) url.searchParams.set("limit", String(query.limit));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters) url.searchParams.set("filters", JSON.stringify(query.filters));
    if (query.serviceStandardId) url.searchParams.set("serviceStandardId", query.serviceStandardId);
    if (query.positionId) url.searchParams.set("positionId", query.positionId);
    if (query.checklistId) url.searchParams.set("checklistId", query.checklistId);
    if (query.qcStandardId) url.searchParams.set("qcStandardId", query.qcStandardId);
    if (query.kpiId) url.searchParams.set("kpiId", query.kpiId);
    if (query.search) url.searchParams.set("search", query.search);
    return fetcher.get<ServiceStandardMappingListResult>(url.toString());
  },

  async getMappings(serviceStandardId: string): Promise<{ data: ServiceStandardMappingDTO[] }> {
    return fetcher.get<{ data: ServiceStandardMappingDTO[] }>(`${baseUrl}/${serviceStandardId}/mappings`);
  },

  async createMapping(
    serviceStandardId: string,
    data: {
      positionId?: string | null;
      checklistId?: string | null;
      qcStandardId?: string | null;
      kpiId?: string | null;
    },
  ): Promise<ServiceStandardMappingDTO> {
    return fetcher.post<ServiceStandardMappingDTO>(`${baseUrl}/${serviceStandardId}/mappings`, data);
  },

  async updateMapping(
    id: string,
    data: {
      positionId?: string | null;
      checklistId?: string | null;
      qcStandardId?: string | null;
      kpiId?: string | null;
    },
  ): Promise<ServiceStandardMappingDTO> {
    return fetcher.patch<ServiceStandardMappingDTO>(`${baseUrl}/mappings/${id}`, data);
  },

  async deleteMapping(id: string): Promise<{ ok: true }> {
    return fetcher.delete<{ ok: true }>(`${baseUrl}/mappings/${id}`);
  },
};

export type ServiceChannelDTO = {
  id: string;
  name: string;
  description: string | null;
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
};

export type ServiceChannelsListResult = {
  data: ServiceChannelDTO[];
  meta: { total: number; limit: number; offset: number };
};

export const serviceChannelApi = {
  async list(query: Partial<OffsetPageQueryDTO> = {}): Promise<ServiceChannelsListResult> {
    const url = new URL(`${baseUrl}/service-channels`);
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.offset) url.searchParams.set("offset", String(query.offset));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<ServiceChannelsListResult>(url.toString());
  },

  async get(id: string): Promise<ServiceChannelDTO> {
    return fetcher.get<ServiceChannelDTO>(`${baseUrl}/service-channels/${id}`);
  },

  async create(input: { name: string; description?: string | null; orderIndex?: number }): Promise<ServiceChannelDTO> {
    return fetcher.post<ServiceChannelDTO>(`${baseUrl}/service-channels`, input);
  },

  async update(id: string, input: { name?: string; description?: string | null; orderIndex?: number }): Promise<ServiceChannelDTO> {
    return fetcher.patch<ServiceChannelDTO>(`${baseUrl}/service-channels/${id}`, input);
  },

  async delete(id: string): Promise<{ ok: true }> {
    return fetcher.delete<{ ok: true }>(`${baseUrl}/service-channels/${id}`);
  },

  async lookup(params: {
    query: string;
    cursor?: string | null;
    pageSize: number;
  }) {
    return fetchLookup(`${baseUrl}/service-channels/lookup`, params);
  },

  async hydrate(id: string) {
    return hydrateLookupItem(`${baseUrl}/service-channels/lookup`, id);
  },
};

export const departmentApi = {
  async lookup(params: {
    query: string;
    cursor?: string | null;
    pageSize: number;
  }) {
    return fetchLookup(`${baseUrl}/departments/lookup`, params);
  },

  async hydrate(id: string) {
    return hydrateLookupItem(`${baseUrl}/departments/lookup`, id);
  },
};

